import { useParameterStore } from '@/frontend/store/ParameterStore';
import { ROOT_TYPES } from '@/types/containerTypes';
import { useProcedureListStore } from '@/frontend/store/ProcedureListStore';
import { useProcedureInstanceStore } from '@/frontend/store/ProcedureInstanceStore';
import { useTreeStore } from '@/frontend/store/TreeStore';
import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProcedurePlaceholder } from '../containers/ContainersPlaceholder';
import { useBlockStore } from '@/frontend/store/BlockStore';
import { useFunctionalInterfacesStore } from '@/frontend/store/FunctionalInterfaceStore';
import { useEquipmentStore } from '@/frontend/store/EquipmentStore';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { NotFound } from '@/frontend/pages/NotFound';
import { useInstrumentStore } from '@/frontend/store/InstrumentStore';

export function ProcedureItemLoader(props: { children: ReactNode}) {
  const { children } = props;

  const resetTree = useTreeStore((state) => state.reset);
  const resetEquipment = useEquipmentStore((state) => state.resetEquipment);
  const resetParameters = useParameterStore((state) => state.reset);
  const resetProcedure = useProcedureInstanceStore((state) => state.reset);
  const procedure = useProcedureInstanceStore((state) => state.procedureInstance);
  const fetchProcedure = useProcedureInstanceStore((state) => state.fetchProcedureInstance);
  const resetListStore = useProcedureListStore((state) => state.reset);
  const viewProcedure = useProcedureInstanceStore((state) => state.viewProcedureInstance);
  const fetchRootItemChildren = useTreeStore((state) => state.fetchRootItemChildren);
  const fetchCommandingRunsByProcedure = useTreeStore((state) => state.fetchCommandingRunsByProcedure);
  const listBlocksWithChildren = useBlockStore((state) => state.listBlocksWithChildren);
  const resetBlocks = useBlockStore((state) => state.reset);
  const setObserver = useTreeStore((state) => state.setObserver);
  const loadNode = useTreeStore((state) => state.loadNode);
  const [loading, setLoading] = useState<boolean>(true);
  const fetchContextLogs = useParameterStore((state) => state.fetchContextLogs);
  const fetchAllLatestFunctionalInterfaceVersions = useFunctionalInterfacesStore((state) => state.fetchAllLatestVersions);
  const readVersionsForProcedure = useFunctionalInterfacesStore((state) => state.readVersionsForProcedure);
  const resetFunctionalInterfaces = useFunctionalInterfacesStore((state) => state.reset);
  const resetInstruments = useInstrumentStore((state) => state.reset);
  const fetchEquipmentForProcedureInstance = useEquipmentStore((state) => state.fetchEquipmentForProcedureInstance);
  const fetchEquipmentGroupsForProcedureInstance = useEquipmentStore((state) => state.fetchEquipmentGroupsForProcedureInstance);
  const fetchEquipmentGroupAssignmentsForProcedureInstance = useEquipmentStore(
    (state) => state.fetchEquipmentGroupAssignmentsForProcedureInstance,
  );
  const user = useGlobalStore((state) => state.userClass);

  const params = useParams();
  const procedureId = params.procedureId as string;

  useEffect(() => {
    void fetchProcedure(procedureId).then(() => {
      useTreeStore.setState({ rootId: procedureId, rootType: ROOT_TYPES.PROCEDURE });
      const fetch = async () => {
        await Promise.all([
          fetchRootItemChildren(procedureId),
          listBlocksWithChildren(),
          fetchContextLogs(procedureId),
          readVersionsForProcedure(procedureId),
          fetchEquipmentForProcedureInstance(procedureId),
          fetchEquipmentGroupsForProcedureInstance(procedureId),
          fetchEquipmentGroupAssignmentsForProcedureInstance(procedureId),
          fetchCommandingRunsByProcedure(procedureId),
        ]);
        setLoading(false);
      };

      void fetch();
    });

    void viewProcedure(procedureId);

    void fetchAllLatestFunctionalInterfaceVersions();

    return () => {
      resetTree();
      resetEquipment();
      resetProcedure();
      resetListStore();
      resetParameters();
      resetBlocks();
      resetFunctionalInterfaces();
      resetInstruments();
    };
  }, [
    procedureId,
    fetchProcedure,
    resetTree,
    resetProcedure,
    resetListStore,
    viewProcedure,
    resetParameters,
    fetchContextLogs,
    fetchEquipmentForProcedureInstance,
    fetchEquipmentGroupsForProcedureInstance,
    fetchEquipmentGroupAssignmentsForProcedureInstance,
    fetchRootItemChildren,
    fetchCommandingRunsByProcedure,
    listBlocksWithChildren,
    readVersionsForProcedure,
    resetEquipment,
    resetBlocks,
    resetFunctionalInterfaces,
    resetInstruments,
  ]);

  const callback = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const containerId = entry.target.id;
      const navItem = document.getElementById(`tree-item-${containerId}`);
      if (entry.isIntersecting) {
        if (navItem) {
          navItem.classList.add('bg-body-secondary');
          navItem.classList.add('bg-opacity-50');
          navItem.classList.add('shadow-sm');
        }
        loadNode(containerId);
      } else if (navItem) {
        navItem.classList.remove('bg-body-secondary');
        navItem.classList.remove('bg-opacity-50');
        navItem.classList.remove('shadow-sm');
      }
    });
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: .001,
    };
    const observer = new IntersectionObserver(callback, options);
    setObserver(observer);
    return () => {
      observer.disconnect();
    };
  }, []);

  if (loading) {
    return <ProcedurePlaceholder />;
  }

  if(!procedure || procedure.orgId !== user?.getOrgId()) {
    return <NotFound />;
  }

  return (
    <div className='container-fluid d-flex h-100' style={{paddingRight: '0', paddingLeft: '0'}}>
      { children }
    </div>
  );
}
